import { Injectable, inject } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { BehaviorSubject, map, Observable, take } from 'rxjs';
import { ProductCategories } from '../_enums/product-categories';
import { GET_COLLECTION_BY_HANDLE, GET_PRODUCTS_BY_KEYWORD } from '../_graphql/collections';
import { IShopifyImage, IShopifyProduct, IShopifyProductVariant, IShopifySeo } from '@app/_shared/_models';

@Injectable({
  providedIn: 'root'
})
export class CollectionService {
  private readonly apolloService = inject(Apollo);

  public bestSellerProducts$!: Observable<Array<IShopifyProduct>>;
  public products$!: Observable<Array<IShopifyProduct>>;
  public apolloObs$!: Observable<any>;

  private readonly _bestSellerProducts: BehaviorSubject<Array<IShopifyProduct>>;
  private readonly _products: BehaviorSubject<Array<IShopifyProduct>>;

  constructor() {
    this._bestSellerProducts = new BehaviorSubject<Array<IShopifyProduct>>([]);
    this._products = new BehaviorSubject<Array<IShopifyProduct>>([]);
    this.bestSellerProducts$ = this._bestSellerProducts.asObservable();
    this.products$ = this._products.asObservable();
  }

  public loadBestSellers(): void {
    this.getBestSellers()
      .pipe(take(1))
      .subscribe((result: any) => {
        this._bestSellerProducts.next([]);
        const products = CollectionService.getProductsFromQuery(result);

        this._bestSellerProducts.next(products);
      });
  }

  public loadProducts(query: string): void {
    this.getProducts(query)
      .pipe(take(1))
      .subscribe((result: any) => {
        this._products.next([]);
        const products = CollectionService.getProductsFromQuery(result);

        this._products.next(products);
      });
  }

  public searchProductsByKeyword(keyword: string): void {
    this.getProductsByKeyword(keyword)
      .pipe(take(1))
      .subscribe((result: any) => {
        this._products.next([]);
        const products = CollectionService.getProductsFromQuery(result);

        this._products.next(products);
      });
  }

  public getCollectionProducts(handle: string): Observable<Array<IShopifyProduct>> {
    return this.getProducts(handle)
      .pipe(map((result: any) => CollectionService.getProductsFromQuery(result)));
  }

  private getBestSellers(): Observable<any> {
    return this.apolloService
      .watchQuery({
        query: GET_COLLECTION_BY_HANDLE,
        variables: {
          handle: 'best-sellers'
        }
      })
      .valueChanges.pipe(take(1));
  }

  private getProducts(query: string): Observable<any> {
    return this.apolloService
      .watchQuery({
        query: GET_COLLECTION_BY_HANDLE,
        variables: {
          handle: query
        }
      })
      .valueChanges.pipe(take(1));
  }

  private getProductsByKeyword(query: string): Observable<any> {
    const keyword = `*${query}*`;

    return this.apolloService
      .watchQuery({
        query: GET_PRODUCTS_BY_KEYWORD,
        variables: {
          keyword: keyword
        }
      })
      .valueChanges.pipe(take(1));
  }

  private static getProductsFromQuery(result: any): Array<IShopifyProduct> {
    let products = new Array<IShopifyProduct>();
    let responseData = result.data?.collection?.products?.edges ?? result.data?.products?.edges;
    if (responseData?.length > 0) {
      responseData.forEach((edge: any) => {
        if (edge?.node && edge?.node.featuredImage) {
          const node = edge?.node;
          const image: IShopifyImage = {
            altText: node.featuredImage.altText,
            url: node.featuredImage.url,
            width: node.featuredImage.width,
            height: node.featuredImage.height,
            id: node.featuredImage.id
          }

          let category = ProductCategories.BOTH;

          if (edge.node.tags?.length > 0) {
            if (edge.node.tags?.some((c: string) => c.includes('Pool'))
                  && (edge.node.tags.some((c: string) => c.includes('Hot Tub')) || edge.node.tags.some((c: string) => c.includes('Spas')))) {
              category = ProductCategories.BOTH;
            } else if (edge.node.tags.some((c: string) => c.includes('Pool'))) {
              category = ProductCategories.POOL;
            } else if (edge.node.tags.some((c: string) => c.includes('Hot Tub')) || edge.node.tags.some((c: string) => c.includes('Spas'))) {
              category = ProductCategories.SPA;
            }
          }

          const variants = new Array<IShopifyProductVariant>();
          node.variants?.edges?.forEach((variantEdge: any) => {
            const variantNode = variantEdge?.node;

            const variantImage: IShopifyImage = {
              altText: variantNode.image.altText,
              url: variantNode.image.url,
              width: variantNode.image.width,
              height: variantNode.image.height,
              id: variantNode.image.id
            }

            const variant: IShopifyProductVariant = {
              availableForSale: variantNode.availableForSale,
              amountInCart: 0,
              id: variantNode.id,
              image: variantImage,
              inventory: variantNode.quantityAvailable,
              sku: variantNode.sku,
              variantTitle: variantNode.title ?? '',
              productTitle: node.title ?? '',
              price: variantNode.price?.amount != null ? +variantNode.price.amount : 0,
              weight: variantNode.weight,
              weightUnit: variantNode.weightUnit,
              sellingPlanAllocations: []
            }

            variants.push(variant);
          });

          const seo: IShopifySeo = {
            description: node.seo?.description ?? '',
            title: node.seo?.title ?? ''
          }

          const product: IShopifyProduct = {
            descriptionHtml: node.descriptionHtml ?? '',
            handle: node.handle ?? '',
            id: node.id,
            image,
            inventory: node.totalInventory ?? 0,
            price: variants[0]?.price ?? 0,
            tags: node.tags,
            title: node.title ?? '',
            vendor: node.vendor ?? '',
            category,
            variants,
            seo,
            sellingPlanGroups: []
          }
          products.push(product);
        }
      });
    }

    return products;
  }
}
